import config from "../util/Config";
import {EntityType} from "./Model";
import {useRefreshIndicatorStore} from "../components/refresh-indicator/refreshIndicatorStore";
import {updateWebSocket} from "./UpdateWebSocket";
import {Lifetime, moduleLifetime} from "../util/Lifetime";

type UpdatesConnectionEvent =
    | { type: 'update', entity: EntityType, officeId: number | null }
    | { type: 'reconnect' }

class UpdatesConnection {
    private listeners = new Set<(event: UpdatesConnectionEvent) => void>();

    constructor(private readonly lifetime: Lifetime = new Lifetime()) {
        this.startConnection(this.lifetime)
            .then();
    }

    private async startConnection(lifetime: Lifetime) {
        const cfg = await config;

        const incomingMessages = updateWebSocket({
            url: `${cfg.webSocketUrl}/api/updates`,
            lifetime: lifetime,
        });
        for await (const message of incomingMessages) {
            switch (message.type) {
                case 'connected':
                    useRefreshIndicatorStore.getState()
                        .setConnectionStatus('connected');
                    this.notifyListeners({
                        type: 'reconnect',
                    });
                    break;
                case 'update':
                    this.notifyListeners({
                        type: 'update',
                        entity: message.entity,
                        officeId: message.officeId,
                    });
                    break;
                case 'disconnected':
                    useRefreshIndicatorStore.getState()
                        .setConnectionStatus(navigator.onLine ? 'disconnected' : 'no-internet')
                    break;
                case 'forceRefreshVersion':
                    if (cfg.forceRefreshVersion < message.forceRefreshVersion) {
                        window.location.reload();
                    }
                    break;
            }
        }
    }

    addListener(listener: (event: UpdatesConnectionEvent) => void): () => void {
        this.listeners.add(listener);

        return () => {
            this.listeners.delete(listener);
        };
    }

    private notifyListeners(event: UpdatesConnectionEvent) {
        this.listeners.forEach(listener => {
            try {
                listener(event);
            } catch (e) {
                console.error("Error in update listener:", e);
            }
        });
    }
}

// Singleton instance
const lifetime = moduleLifetime(module);
export const updatesConnection = new UpdatesConnection(lifetime);
